import PropTypes from "prop-types";
import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AdminUserInfo } from "./AdminUserInfo";
import { THEME } from "@shared/constants/designSystem";
import { Bars3Icon, ChevronDownIcon } from "@heroicons/react/24/outline";
import ThemeToggle from "@shared/components/navigation/ThemeToggle";

export const AdminHeader = ({ onLogout, onMenuClick }) => {
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChangePassword = () => {
    setIsUserMenuOpen(false);
    navigate("/admin/change-password");
  };

  const handleLogout = () => {
    setIsUserMenuOpen(false);
    onLogout();
  };

  return (
    <header
      className={`${THEME.COLORS.BG.DEFAULT} shadow-sm sticky top-0 z-[60]`}
    >
      <div className="w-full h-16">
        <div className="h-full px-4 flex items-center">
          {/* Left side */}
          <button
            onClick={onMenuClick}
            className={`md:hidden p-2 rounded-md ${THEME.COLORS.HOVER.BG.DEFAULT}`}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="md:hidden ml-4">
            <h1 className={`text-xl font-bold ${THEME.COLORS.TEXT.DEFAULT}`}>
              Admin Panel
            </h1>
          </div>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Right side */}
          <div className="flex items-center gap-4">
            <ThemeToggle />
            <AdminUserInfo />

            {/* User Menu Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                className={`flex items-center gap-1 px-4 py-2 text-sm rounded-md
                  ${THEME.COLORS.BG.SECONDARY} 
                  ${THEME.COLORS.TEXT.ON_SECONDARY}
                  ${THEME.COLORS.HOVER.BG.SECONDARY}
                  transition-colors duration-200`}
              >
                Menu
                <ChevronDownIcon
                  className={`h-4 w-4 transition-transform ${isUserMenuOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Dropdown Menu */}
              {isUserMenuOpen && (
                <div
                  className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg ${THEME.COLORS.BG.DEFAULT} ring-1 ring-black ring-opacity-5`}
                >
                  <div className="py-1">
                    <button
                      onClick={handleChangePassword}
                      className={`block w-full text-left px-4 py-2 text-sm ${THEME.COLORS.TEXT.DEFAULT} hover:${THEME.COLORS.BG.MUTED}`}
                    >
                      Change Password
                    </button>
                    <hr className={`my-1 ${THEME.COLORS.BORDER.DEFAULT}`} />
                    <button
                      onClick={handleLogout}
                      className={`block w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:${THEME.COLORS.BG.MUTED}`}
                    >
                      Logout
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

AdminHeader.propTypes = {
  onLogout: PropTypes.func.isRequired,
  onMenuClick: PropTypes.func.isRequired,
};
